import { CorporateDto } from 'back-office/features/plateformes-view/api/corporateApi';
import { PersonneMoraleListItemDto } from 'back-office/features/personnes-morales-view/api/personnesMoralesApi';

/**
 * Neutral view-model for a personne morale profile — the same UI (hero,
 * identity, contact cards) serves the governing PM of the Gouvernance tab,
 * the full PM details page and the intermediary quick view, each with its own
 * mapper. Labels are resolved through the `personneMorale` i18n namespace by
 * the rendering components; this model only carries data and i18n keys.
 */
export interface PersonneMoraleProfileData {
  personneMoraleId: string;
  name: string;
  natureLabel?: string | null;
  sousNatureLabel?: string | null;
  /** Plateforme SSO key — governing PM only. */
  companyCode?: string | null;
  siren?: string | null;
  capital?: number | null;
  rcsCity?: string | null;
  rcsNumber?: string | null;
  orias?: string | null;
  rcp?: string | null;
  phone?: string | null;
  email?: string | null;
  /** i18n key (personneMorale ns) for the email label — corporate email vs plain email. */
  emailLabelKey?: string;
  addressInline?: string | null;
  // Detail-GET-only fields — absent on the governance side, rendered only when present.
  /** Back CanalAcquisition enum, serialized as a number (0 = not set). */
  acquisitionChannel?: number | null;
  oriasLastControl?: string | null;
  attachedBroker?: boolean | null;
  comment?: string | null;
}

const inlineAddress = (parts: (string | null | undefined)[]): string | null => {
  const value = parts.filter(Boolean).join(', ');
  return value || null;
};

export const profileFromCorporate = (
  corporate: CorporateDto
): PersonneMoraleProfileData => ({
  personneMoraleId: corporate.personneMoraleId,
  name: corporate.name,
  natureLabel: corporate.natureLabel,
  sousNatureLabel: corporate.sousNatureLabel,
  companyCode: corporate.code,
  siren: corporate.registrationNumber,
  capital: corporate.capital,
  rcsCity: corporate.rcsCity,
  rcsNumber: corporate.rcsNumber,
  orias: corporate.oriasNumber,
  rcp: corporate.rcp,
  phone: corporate.phone,
  email: corporate.email,
  emailLabelKey: 'contact.corporateEmail',
  addressInline: corporate.address
    ? inlineAddress([
        corporate.address.line1,
        corporate.address.line2,
        [corporate.address.zipCode, corporate.address.city]
          .filter(Boolean)
          .join(' '),
        corporate.address.country
      ])
    : null
});

export const profileFromPersonneMorale = (
  pm: PersonneMoraleListItemDto
): PersonneMoraleProfileData => ({
  personneMoraleId: pm.id,
  name: pm.designation,
  natureLabel: pm.natureLibelle ?? pm.nature,
  sousNatureLabel: pm.sousNatureLibelle,
  siren: pm.numeroSiret,
  capital: pm.capital,
  rcsCity: pm.rcsVille,
  rcsNumber: pm.rcsNumero,
  orias: pm.orias,
  rcp: pm.rcp,
  phone: pm.telephone,
  acquisitionChannel: pm.canalAcquisition,
  oriasLastControl: pm.lastControlOrias ?? null,
  attachedBroker: pm.courtierId != null,
  comment: pm.commentaire ?? null,
  addressInline: pm.adresse
    ? inlineAddress([
        pm.adresse.line1,
        pm.adresse.line2,
        [pm.adresse.zipCode, pm.adresse.city].filter(Boolean).join(' '),
        pm.adresse.country
      ])
    : null
});
